import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { TranslateModule } from '@ngx-translate/core';
import { SharedService } from '@services/shared.service';
import { PagesService } from '@services/pages.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

export interface ActivityLogModel {
  id: number;
  created_at: number;
  category: string;
  action: string;
  severity: string;
  description: string;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  content_type: string;
  content_id: number;
  content_title: string;
  target_user_id: number;
  changes: any;
}

@Component({
  selector: 'app-activity-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzToolTipModule,
    NzDrawerModule,
    NzSelectModule,
    NzDatePickerModule,
    TranslateModule,
  ],
  templateUrl: './activity-logs.component.html',
  styleUrls: ['./activity-logs.component.scss']
})
export class ActivityLogsComponent implements OnInit, OnDestroy {
  private sharedService = inject(SharedService);
  private pagesService = inject(PagesService);
  private secureStorage = inject(SecureStorageService);

  logs: ActivityLogModel[] = [];
  totalLogs: number = 0;
  searchTerm: string = '';

  // Pagination
  currentPage: number = 1;
  perPage: number = 20;

  // Sorting
  sortBy: string = '';
  sortOrder: string = '';

  // Filters
  selectedCategory: string = '';
  selectedAction: string = '';
  selectedSeverity: string = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  selectedContentType: string = '';

  // Company ID from logged-in user
  companyId: number = 0;

  // Detail drawer
  drawerVisible: boolean = false;
  selectedLog: ActivityLogModel | null = null;
  changesList: { field: string; old_value: any; new_value: any }[] = [];

  // Filter options
  categoryOptions = [
    { value: 'user_activity', label: 'User Activity' },
    { value: 'contest', label: 'Contest' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'system', label: 'System' },
  ];

  actionOptions = [
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'approve', label: 'Approve' },
    { value: 'reject', label: 'Reject' },
  ];

  severityOptions = [
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error' },
    { value: 'critical', label: 'Critical' },
  ];

  // Debounced search
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadLogs(1, false);
    });

    this.initCompanyId();
    this.loadLogs(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  private initCompanyId(): void {
    try {
      const userDetails = this.secureStorage.getItem<any>('userDetails') || {};
      if (userDetails.isCompany?.length) {
        this.companyId = userDetails.isCompany[0].id;
      }
    } catch {
      this.companyId = 0;
    }
  }

  loadLogs(page: number = 1, showLoader: boolean = true): void {
    if (!this.companyId) return;
    if (showLoader) this.sharedService.showLoader();

    const params: any = {
      page,
      per_page: this.perPage,
      search: this.searchTerm?.trim() || '',
      category: this.selectedCategory || '',
      action: this.selectedAction || '',
      severity: this.selectedSeverity || '',
      content_type: this.selectedContentType || '',
      user_id: this.companyId,
    };

    if (this.dateFrom) {
      params.date_from = this.formatDateParam(this.dateFrom);
    }
    if (this.dateTo) {
      params.date_to = this.formatDateParam(this.dateTo);
    }
    if (this.sortBy) {
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder || 'desc';
    }

    this.pagesService.getAll('ACTIVITY_LOGS', params).subscribe({
      next: (response: any) => {
        // Handle both response formats
        const data = response?.data || response;
        const logs = data?.logs || data?.items || (Array.isArray(data) ? data : []);
        const pagination = data?.pagination;

        this.logs = logs;
        this.totalLogs = pagination?.total_records || data?.itemsTotal || logs.length;
        this.currentPage = pagination?.current_page || data?.curPage || page;
        if (showLoader) this.sharedService.hideLoader();
      },
      error: () => {
        if (showLoader) this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to load activity logs');
      }
    });
  }

  // Pagination handlers
  onPageIndexChange(index: number): void {
    this.currentPage = index;
    this.loadLogs(index);
  }

  onPageSizeChange(size: number): void {
    this.perPage = size;
    this.currentPage = 1;
    this.loadLogs(1);
  }

  // Sort handler
  onSort(field: string, order: string | null): void {
    if (order) {
      this.sortBy = field;
      this.sortOrder = order === 'ascend' ? 'asc' : 'desc';
    } else {
      this.sortBy = '';
      this.sortOrder = '';
    }
    this.currentPage = 1;
    this.loadLogs(1);
  }

  // Filter handlers
  onFilterChange(): void {
    this.currentPage = 1;
    this.loadLogs(1);
  }

  clearFilters(): void {
    this.selectedCategory = '';
    this.selectedAction = '';
    this.selectedSeverity = '';
    this.selectedContentType = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadLogs(1);
  }

  hasActiveFilters(): boolean {
    return !!(this.selectedCategory || this.selectedAction || this.selectedSeverity ||
      this.selectedContentType || this.dateFrom || this.dateTo);
  }

  // Detail drawer
  openDetailDrawer(log: ActivityLogModel): void {
    this.selectedLog = log;
    this.changesList = this.parseChanges(log.changes);
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedLog = null;
    this.changesList = [];
  }

  private parseChanges(changes: any): { field: string; old_value: any; new_value: any }[] {
    if (!changes) return [];
    if (Array.isArray(changes)) {
      return changes.map((c: any) => ({
        field: c.field || c.field_name || '',
        old_value: c.old_value ?? c.old ?? '',
        new_value: c.new_value ?? c.new ?? '',
      }));
    }
    if (typeof changes === 'object') {
      return Object.keys(changes).map(key => ({
        field: key,
        old_value: changes[key]?.old_value ?? changes[key]?.old ?? '',
        new_value: changes[key]?.new_value ?? changes[key]?.new ?? '',
      }));
    }
    return [];
  }

  // CSV Export
  exportCsv(): void {
    if (this.totalLogs === 0) {
      this.sharedService.createNotification('error', 'No logs to export');
      return;
    }

    this.sharedService.showLoader();

    const params: any = {
      page: 1,
      per_page: this.totalLogs,
      search: this.searchTerm?.trim() || '',
      category: this.selectedCategory || '',
      action: this.selectedAction || '',
      severity: this.selectedSeverity || '',
      content_type: this.selectedContentType || '',
      user_id: this.companyId,
    };

    if (this.dateFrom) params.date_from = this.formatDateParam(this.dateFrom);
    if (this.dateTo) params.date_to = this.formatDateParam(this.dateTo);
    if (this.sortBy) {
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder || 'desc';
    }

    this.pagesService.getAll('ACTIVITY_LOGS', params).subscribe({
      next: (response: any) => {
        const data = response?.data || response;
        const allLogs = data?.logs || data?.items || (Array.isArray(data) ? data : []);
        this.generateCsv(allLogs);
        this.sharedService.hideLoader();
      },
      error: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to fetch data for export');
      }
    });
  }

  private generateCsv(logs: ActivityLogModel[]): void {
    const headers = ['Date & Time', 'Category', 'Action', 'Severity', 'Description', 'Content'];
    const rows = logs.map(log => [
      this.formatDateTime(log.created_at),
      log.category || '',
      log.action || '',
      log.severity || '',
      `"${(log.description || '').replace(/"/g, '""')}"`,
      log.content_title || '',
    ]);

    const bom = '\uFEFF';
    const csvContent = bom + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `activity_logs_${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    this.sharedService.createNotification('success', 'CSV exported successfully');
  }

  // Utility methods
  formatDateTime(timestamp: number): string {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  private formatDateParam(date: Date): string {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  getCategoryClass(category: string): string {
    const map: Record<string, string> = {
      user_activity: 'badge-blue',
      contest: 'badge-green',
      subscription: 'badge-yellow',
      system: 'badge-red',
    };
    return map[category] || 'badge-default';
  }

  getSeverityClass(severity: string): string {
    const map: Record<string, string> = {
      info: 'severity-info',
      warning: 'severity-warning',
      error: 'severity-error',
      critical: 'severity-critical',
    };
    return map[severity] || 'severity-info';
  }

  getPageRangeText(): string {
    const start = this.totalLogs === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    const end = Math.min(this.currentPage * this.perPage, this.totalLogs);
    return `${start}-${end} of ${this.totalLogs} items`;
  }
}
